// src/components/shared/InterpretacionChips.jsx
//
// ── Design decisions ──────────────────────────────────────────────────────────
// Superficie de confirmación de las interpretaciones de catalogSearch.
//
// · Un chip ámbar por interpretación: «yinas → sandalias». Ámbar porque es una
//   suposición del sistema, no un dato del gestor — el mismo color que usan
//   "Sin bulto" y "Requiere revisión" en la Encomienda de viaje.
// · Ícono por tipo: Languages para jerga (palabra reinterpretada), Tag para
//   marca (tier detectado). Así el gestor distingue de un vistazo qué clase de
//   suposición está confirmando.
// · El title explica en español QUÉ se interpretó y pide verificar; el ✕
//   (solo si hay onDescartar) es el objetivo táctil de 44 px, no el chip entero,
//   para no disparar descartes por roce en tablet.
// · Sin estado propio: quién decide si la interpretación sigue viva es el
//   llamador (el drawer), que recibe onDescartar(interpretacion).
// · Renderiza null con lista vacía: el llamador lo monta sin condicionar.
// · `ambiance` (lo pide el panel de revisión de extracción) cambia la piel por
//   los tokens de ambiance.css: la suposición pasa al azul de aviso de ese
//   panel, que ahí es el color de "revisá esto". Sin el prop, el chip ámbar
//   queda exactamente como está.
// ─────────────────────────────────────────────────────────────────────────────

import { Languages, Tag, X } from 'lucide-react';

const ICONO = {
  jerga: Languages,
  marca: Tag,
};

const TIPO_TEXTO = {
  jerga: 'jerga',
  marca: 'marca',
};

export const textoAyuda = (i) =>
  `Interpretamos '${i.original}' como '${i.interpretado}' — verificá que sea correcto`;

const InterpretacionChips = ({
  interpretaciones = [],
  onDescartar = null,
  className = '',
  ambiance = false,
}) => {
  if (!Array.isArray(interpretaciones) || interpretaciones.length === 0) return null;

  const clsChip = ambiance
    ? 'amb-chip amb-chip--interp pl-2 pr-1'
    : 'inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 py-0.5 pl-2 pr-1 text-[11px] font-medium text-amber-800';
  const clsCerrar = ambiance
    ? '-my-2 -mr-1 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full amb-link'
    : '-my-2 -mr-1 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-amber-700 hover:bg-amber-100 hover:text-amber-900';

  return (
    <ul
      aria-label="Interpretaciones de la descripción"
      className={`flex flex-wrap items-center gap-1.5 ${className}`}
    >
      {interpretaciones.map((i, idx) => {
        const Icono = ICONO[i.tipo] || Languages;
        const clave = `${i.tipo}-${i.original}-${idx}`;
        return (
          <li
            key={clave}
            title={textoAyuda(i)}
            className={clsChip}
          >
            <Icono
              size={12}
              aria-label={i.tipo === 'marca' ? 'Tier de marca detectado' : 'Jerga interpretada'}
              className="shrink-0"
            />
            <span>
              <span className="font-semibold">{i.original}</span>
              <span aria-hidden="true"> → </span>
              <span className="sr-only"> interpretado como </span>
              <span>{i.interpretado}</span>
            </span>
            <span className="sr-only"> ({TIPO_TEXTO[i.tipo] || i.tipo})</span>
            {typeof onDescartar === 'function' ? (
              <button
                type="button"
                onClick={() => onDescartar(i)}
                aria-label={`Descartar interpretación de '${i.original}'`}
                title="Descartar esta interpretación"
                className={clsCerrar}
              >
                <X size={12} aria-hidden="true" />
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
};

export default InterpretacionChips;
